import { AccessRightFilterCollectionModel, PublicationSettingModel } from '@fi-shared/models';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiPublicationActionType } from '@fi-shared/types';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { CheckpointModel } from './checkpoint.model';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { ContentLanguageLabelModel } from '@fi-shared/models';
import { isUndefined } from 'util';
import { isString } from '@fi-shared/utils/is-string';
import { CheckpointStatusType } from '@fi-shared/checklist';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { Injector } from '@angular/core';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

export class ChecklistModel {
    Id: number;
    CreatedByUserId: number;
    CreatedByUserName: string;
    CreatedOn: any;
    ChangedByUserId: number;
    ChangedByUserName: string;
    ChangedOn: any;
    Name: string;
    Description: string;
    CheckPresenceEnabled: boolean;
    IsTemplate: boolean;
    IsSetNotApplicable: boolean;
    IsTurnOffReminder: boolean;
    IsPublished: boolean;
    IsWithResponses: boolean;
    //AnswersCount: number;
    IsActive: boolean;
    HasEditAccess: boolean;
    TimeSetting: number;
    RepeatFrequency: number;
    RecurringDays: number[];
    RecurringHours: number[];
    RecurringMonths: number[];
    PreviewFileName: string;
    DueTime: { Days: number; Hours: number; Months: number };
    RemindTime: { Days: number; Hours: number; Months: number };
    CurrentPeriodId: number;
    MediaBankFileReferences: IMediaBankFileReference[];
    DueDateTime: Date;
    StartDateTime: Date;
    RemindDateTime: Date;
    IsShowReportsToOtherUsers: boolean;
    CheckListSetting: any;
    Names: ContentLanguageLabelModel[] = [];
    Descriptions: ContentLanguageLabelModel[] = [];
    CheckPoints: CheckpointModel[];
    PublicationSetting: PublicationSettingModel;
    AccessRightFilterCollection: AccessRightFilterCollectionModel;
    AnswersStatus: number;
    LastActivePeriodStartDate: string;
    LastActivePeriodEndDate: string;
    HasReportsAccess: boolean;
    BusinessUnitSettingId: number;
    IsVisited: boolean = false;

    constructor(data, injector: Injector) {
        if (!data) data = {};

        this.init(data, injector);
    }

    init(data, injector: Injector) {
        this.Id = undefined;
        this.CreatedByUserId = undefined;
        this.CreatedByUserName = undefined;
        this.CreatedOn = undefined;
        this.ChangedByUserId = undefined;
        this.ChangedByUserName = undefined;
        this.ChangedOn = undefined;
        this.Name = undefined;
        this.Description = undefined;
        this.IsSetNotApplicable = undefined;
        this.IsTurnOffReminder = undefined;
        this.IsPublished = undefined;
        this.IsWithResponses = undefined;
        this.HasEditAccess = undefined;
        this.IsShowReportsToOtherUsers = undefined;
        this.TimeSetting = ChecklistModel.TimeSettings.Recurring;
        this.RepeatFrequency = undefined;
        this.RecurringDays = [];
        this.RecurringHours = [];
        this.RecurringMonths = [];
        this.RemindDateTime = null;
        this.PreviewFileName = undefined;
        this.DueTime = {
            Days: undefined,
            Hours: undefined,
            Months: undefined,
        };
        this.RemindTime = {
            Days: undefined,
            Hours: undefined,
            Months: undefined,
        };
        this.CurrentPeriodId = undefined;

        Object.assign(this, data);
        if (!this.MediaBankFileReferences) this.MediaBankFileReferences = [];

        if (this.RemindDateTime && isString(this.RemindDateTime)) this.RemindDateTime = new Date(this.RemindDateTime);

        if (isUndefined(this.IsShowReportsToOtherUsers)) this.IsShowReportsToOtherUsers = true;

        const customerDataService: FiCustomerDataService = injector.get(FiCustomerDataService);
        this.CheckListSetting = {
            IsNews: customerDataService.customerData.CanShareAsNews && false, //https://chainformation.atlassian.net/browse/FI-1347 3) Remove the option "Show as news and on start page"
            SaveSettingsAsNewTemplate: false,
        };
        Object.assign(this.CheckListSetting, data.CheckListSetting);

        const contentLanguagesService = injector.get(ContentLanguagesService);
        contentLanguagesService.InitializeContentLanguagesArray(this.Names, data ? data.Names : []);
        contentLanguagesService.InitializeContentLanguagesArray(this.Descriptions, data ? data.Descriptions : []);

        this.CheckPoints = [];
        (data.CheckPoints || []).sort(propComparator('SortIndex')).forEach((checkpointData) => {
            this.CheckPoints.push(new CheckpointModel(checkpointData, injector));
        });

        this.PublicationSetting = new PublicationSettingModel(ObjectTypes.CheckList);
        this.PublicationSetting.IsShared = true;
        this.PublicationSetting.PublicationActionType = FiPublicationActionType.UnpublishDocument;
        if (data.PublicationSetting) {
            this.PublicationSetting.Initialize(data.PublicationSetting);
        } else {
            this.PublicationSetting.PublicationEndDate = new Date();
            this.PublicationSetting.PublicationEndDate.setHours(23, 59, 59, 0);
            this.PublicationSetting.PublicationEndDate.setMonth(
                this.PublicationSetting.PublicationEndDate.getMonth() + 12,
            );
        }

        this.AccessRightFilterCollection = new AccessRightFilterCollectionModel();
        //this.AccessRightFilterCollection.SelectionMode = AccessRightTabs.ManualChoice;
        if (data.AccessRightFilterCollection)
            this.AccessRightFilterCollection.InitializeAccessRightFilterCollection(data.AccessRightFilterCollection);
    }

    initFromTemplate(checklistTemplate, injector: Injector) {
        const id = this.Id;
        const AccessRightFilterCollection = this.AccessRightFilterCollection,
            ChangedByUserId = this.ChangedByUserId;

        checklistTemplate.PublicationSetting = null; //https://chainformation.atlassian.net/browse/FI-2904
        this.init(checklistTemplate, injector);
        this.Id = id;
        this.AccessRightFilterCollection = AccessRightFilterCollection;
        this.ChangedByUserId = ChangedByUserId;
        this.IsTemplate = false;
        this.CheckPoints.forEach((checkPoint) => {
            checkPoint.Id = undefined;
            if (checkPoint.NotificationRoleId) {
                checkPoint.$$notifyRoleChecked = true;
            }
        });
    }

    static TimeSettings = {
        Recurring: 1,
        OneTime: 2,
    };

    static RepeatFrequency = {
        Monthly: 1,
        Daily: 2,
    };

    static Answers = {
        Ok: CheckpointStatusType.Ok,
        NotOk: CheckpointStatusType.NotOk,
    };

    static Status = {
        Failed: 4,
        Deviation: 3,
        Ok: 2,
        NotFilledOut: 1,
        OkCheckpoints: 'ok', // using only on frontend,
        DeviationCheckpoints: 'deviation', // using only on frontend,
    };

    static StatusColors = {};
    static StatusTextIds = {};
    static StatusFullTextIds = {};
    static StatusMobileTextIds = [];
    static StatusPointsPerUnitTextIds = {};

    static staticInitialize() {
        ChecklistModel.StatusColors[ChecklistModel.Status.NotFilledOut] = '#b8b8b8';
        ChecklistModel.StatusColors[ChecklistModel.Status.Ok] = '#5aaf4b';
        ChecklistModel.StatusColors[ChecklistModel.Status.Deviation] = '#cbe6c8';
        ChecklistModel.StatusColors[ChecklistModel.Status.Failed] = '#f44336';
        ChecklistModel.StatusColors[ChecklistModel.Status.OkCheckpoints] = '#FFECB3';
        ChecklistModel.StatusColors[ChecklistModel.Status.DeviationCheckpoints] = '#FFC107';

        ChecklistModel.StatusTextIds[ChecklistModel.Status.NotFilledOut] = 'checklist.status_not_filled_out';
        ChecklistModel.StatusTextIds[ChecklistModel.Status.Ok] = 'checklist.status_ok';
        ChecklistModel.StatusTextIds[ChecklistModel.Status.Deviation] = 'checklist.status_deviation';
        ChecklistModel.StatusTextIds[ChecklistModel.Status.Failed] = 'checklist.status_failed';

        ChecklistModel.StatusFullTextIds[ChecklistModel.Status.NotFilledOut] = 'checklist.status_not_filled_out';
        ChecklistModel.StatusFullTextIds[ChecklistModel.Status.Ok] = 'checklist.status_ok_full';
        ChecklistModel.StatusFullTextIds[ChecklistModel.Status.Deviation] = 'checklist.status_deviation_full';
        ChecklistModel.StatusFullTextIds[ChecklistModel.Status.Failed] = 'checklist.status_failed_full';

        ChecklistModel.StatusMobileTextIds[ChecklistModel.Status.Ok] = 'checklist.status_ok_mobile';
        ChecklistModel.StatusMobileTextIds[ChecklistModel.Status.Deviation] = 'checklist.status_deviation_mobile';

        ChecklistModel.StatusPointsPerUnitTextIds[ChecklistModel.Status.NotFilledOut] =
            'checklist.points_per_unit.status_not_filled_out';
        ChecklistModel.StatusPointsPerUnitTextIds[ChecklistModel.Status.Ok] = 'checklist_reports.ok_checkpoint';
        ChecklistModel.StatusPointsPerUnitTextIds[ChecklistModel.Status.Deviation] =
            'checklist.points_per_unit.status_deviation';
        ChecklistModel.StatusPointsPerUnitTextIds[ChecklistModel.Status.Failed] =
            'checklist.points_per_unit.status_failed';
        ChecklistModel.StatusPointsPerUnitTextIds[ChecklistModel.Status.OkCheckpoints] = 'checklist.ok_checkpoints';
        ChecklistModel.StatusPointsPerUnitTextIds[ChecklistModel.Status.DeviationCheckpoints] =
            'checklist.checkpoint_deviations_per_unit';
    }
}

ChecklistModel.staticInitialize();
